import type { PluginManifest, ResolvedMenuGroup } from '../../plugins/types';
import type { QuickAccessItem } from '../../contexts/QuickAccessContext';
import type { LauncherSearchItem, BuildLauncherIndexInput } from './types';

/**
 * Resolve the category path (subtitle) for a plugin by finding it in the menu structure.
 * e.g. "AI & Automation > AI Agents" for agent-hub
 */
function resolveCategoryPath(
  pluginId: string,
  menuGroups: ResolvedMenuGroup[],
): { group?: string; section?: string; subtitle?: string } {
  for (const group of menuGroups) {
    if (group.type === 'mega' && group.columns) {
      for (const col of group.columns) {
        const found = col.items.find(item => item.id === pluginId);
        if (found) {
          return {
            group: group.group,
            section: col.title,
            subtitle: `${group.group} \u203A ${col.title}`,
          };
        }
      }
    }
    if (group.type === 'dropdown' && group.items) {
      const found = group.items.find(item => item.id === pluginId);
      if (found) {
        return {
          group: group.group,
          section: undefined,
          subtitle: group.group,
        };
      }
    }
  }
  return {};
}

export function buildLauncherIndex(input: BuildLauncherIndexInput): LauncherSearchItem[] {
  const { manifests, menuGroups, quickAccessItems, pinnedIds } = input;

  const quickAccessMap = new Map<string, QuickAccessItem>();
  for (const item of quickAccessItems) {
    quickAccessMap.set(item.pluginId, item);
  }

  const seen = new Set<string>();
  const items: LauncherSearchItem[] = [];

  for (const manifest of manifests) {
    if (seen.has(manifest.id)) continue;
    seen.add(manifest.id);

    if (manifest.enabled === false) continue;

    const categoryInfo = resolveCategoryPath(manifest.id, menuGroups);
    const quickAccess = quickAccessMap.get(manifest.id);

    const item: LauncherSearchItem = {
      id: manifest.id,
      type: 'plugin',
      title: manifest.title ?? manifest.name ?? manifest.id,
      subtitle: categoryInfo.subtitle,
      description: manifest.description,
      icon: manifest.icon,
      route: manifest.primary_route ?? `/${manifest.id}`,
      group: categoryInfo.group ?? manifest.group,
      section: categoryInfo.section,
      aliases: manifest.aliases ?? [],
      taskPhrases: manifest.task_phrases ?? [],
      keywords: manifest.keywords ?? [],
      lastAccessedAt: quickAccess?.accessedAt,
      accessCount: quickAccess?.accessCount,
      pinned: pinnedIds.has(manifest.id),
      disabled: false,
    };

    items.push(item);
  }

  return items;
}
